import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import Risksystem3PublicAuthService from './risksystem3-public-auth-service';
import TokenService from './token-service';
import Cookies from 'js-cookie';
import { FAST_API_URL } from '../utilities/apiConstants';

const AUTH_ENDPOINTS_WITHOUT_REFRESH = [
  '/v1/token',
  '/v1/verification_code',
  '/v1/get_verification_code',
  '/v1/refresh',
];

const shouldSkipTokenRefresh = (url?: string) =>
  !url ||
  AUTH_ENDPOINTS_WITHOUT_REFRESH.some((endpoint) => url.includes(endpoint));

interface RetriableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const logoutAndReject = (error: unknown) => {
  Risksystem3PublicAuthService.FastAPILogout('session_expired');
  Cookies.remove('user_id');
  return Promise.reject(error);
};

const FastAPIInstance = axios.create({
  baseURL: FAST_API_URL,
  withCredentials: true,
});

// Unsure why the interceptors are not setting the token to the correct token
export function updateAxiosHeaders(
  axiosInstance: AxiosInstance,
  token: string | null,
) {
  if (token) {
    axiosInstance.defaults.headers['Authorization'] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers['Authorization'];
  }
}

// // Before making request, do the following
FastAPIInstance.interceptors.request.use(
  (config) => {
    const token = TokenService.getLocalAccessToken();

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// With response data, do the following
FastAPIInstance.interceptors.response.use(
  (res) => {
    return res;
  },
  async (err) => {
    const originalRequest = err.config as RetriableAxiosRequestConfig;

    if (err.response) {
      // access token expired
      if (
        err.response.status === 401 &&
        !originalRequest._retry &&
        !shouldSkipTokenRefresh(originalRequest.url)
      ) {
        // handle infinite loop
        originalRequest._retry = true;

        const refreshToken = TokenService.getLocalRefreshToken();
        if (!refreshToken) {
          return logoutAndReject(err);
        }

        try {
          const resp = await axios.get(
            `${FAST_API_URL}/v1/refresh?refresh_token=${encodeURIComponent(refreshToken)}`,
            { withCredentials: true },
          );

          const access_token = resp.data.access_token;

          TokenService.updateNewAccessToken(access_token);

          FastAPIInstance.defaults.headers.common['Authorization'] =
            `Bearer ${access_token}`;

          return FastAPIInstance(originalRequest);
        } catch (_error) {
          return logoutAndReject(_error);
        }
      }
    }

    return Promise.reject(err);
  },
);

export default FastAPIInstance;
